/**
 * 產品參數版本（批151 C2）——對應後端 `app/api/admin_product_params.py`。
 *
 * 有些產品要在**不重簽授權**的情況下，讓授權中心統一調整一組數值參數，而且要能預約生效。
 * 鍵名、中文名、單位全部由產品資料宣告（`products.options.versioned_params`）；授權中心只管
 * 驗證、排版本、在心跳時下發「目前生效的一版＋全部未來版本」。
 *
 * 權限：讀＝admin 以上；寫（宣告與版本）＝營運主管以上。代理商不開放。
 * 錯誤一律 `{"detail": {"code", "message", …}}`（message 已是中文；`lib/productParams.ts::
 * paramErrorMessage` 統一取用）。
 */
import { api } from './client'
import type { ProductSpecWithWarnings } from './ls'

/** 宣告的一個參數鍵（後端正規化後四個鍵都會帶齊）。 */
export interface ParamKeyDecl {
  /** 小寫英數與底線、字母開頭、最長 40 字。 */
  key: string
  label: string
  unit: string | null
  hint: string | null
}

/** `products.options.versioned_params` 的形狀。 */
export interface ParamDeclaration {
  version: 1
  keys: ParamKeyDecl[]
}

/** `future`＝尚未生效（可改可刪）｜`current`＝目前生效｜`past`＝已被取代。 */
export type ParamVersionState = 'future' | 'current' | 'past'

export interface ParamVersion {
  /** `v<N>`，由授權中心產生、永不重用。 */
  version_id: string
  /** 秒精度 UTC（`2026-11-01T00:00:00Z`）。 */
  effective_from: string
  /** 參數鍵 → 十進位字串（標準形）。 */
  values: Record<string, string>
  note: string | null
  state: ParamVersionState
  /** 只有未生效的才可改可刪。 */
  editable: boolean
  /** 防線：缺了哪些宣告鍵（正常流程恆為空；有值代表有人繞過服務層改了 DB）。 */
  missing_keys: string[]
  /** 宣告已移除、但版本裡還留著的鍵（不下發）。 */
  extra_keys: string[]
  created_by: string | null
  created_at: string | null
  updated_at: string | null
}

export interface ParamVersionList {
  product: string
  /** `null`＝這個產品還沒有宣告參數鍵。 */
  declaration: ParamDeclaration | null
  /** 新到舊（`effective_from` 由晚到早），最多 200 版；`total` 實算。 */
  items: ParamVersion[]
  total: number
  current_version_id: string | null
  /** 尚未生效的版本最多幾版。 */
  max_future_versions: number
  /** 伺服器現在時刻（UTC，秒精度）。 */
  server_time: string
}

/** 宣告寫入後的自動補版結果（主人裁示 121：可下發的版本永遠完整）。 */
export interface ParamBackfill {
  /** 以「目前生效版＋新鍵初始值」自動建立、立即生效的新版本；沒有補版＝null。 */
  created_version_id: string | null
  /** 就地補上新鍵初始值的未來版本。 */
  patched_version_ids: string[]
  /** 送了但用不到的初始值（不寫入）。 */
  unused_initial_values: string[]
}

/**
 * 目前生效／未來版本裡**缺宣告鍵**的一版（後端 `services/product_params.incomplete_versions`）。
 * 那幾版不會下發；宣告端點會自動補版，正常流程恆為空（有東西＝有人繞過服務層改了 DB）。
 */
export interface IncompleteParamVersion {
  version_id: string
  /** 只會是目前生效或未來的版本（已被取代的不檢查）。 */
  state: Exclude<ParamVersionState, 'past'>
  missing_keys: string[]
}

/**
 * 宣告端點（PUT／DELETE `versioned-params`）回的產品本體：`warnings` 有
 * `param_versions_incomplete` 時 `incomplete_versions` 列出是哪幾版（後端兩支都帶這個鍵；
 * 移除宣告後恆為空陣列）。
 */
export type DeclarationSpec = ProductSpecWithWarnings & {
  incomplete_versions: IncompleteParamVersion[]
}

export type DeclarationResult = DeclarationSpec & {
  backfill: ParamBackfill
}

/**
 * 版本的 body（`effective_from` 帶時區的 ISO 8601；`values` 必須填滿全部宣告鍵）。
 *
 * **新增**時 `effective_from` 可以省略＝**立即生效**（伺服器現在時刻；同一秒已有版本就往後挪
 * 一秒，不佔「未來版本」名額）。修改未生效版本時必填。
 */
export interface ParamVersionInput {
  effective_from?: string
  values: Record<string, string>
  note?: string | null
}

const base = (productId: string) => `/api/admin/products/${encodeURIComponent(productId)}`

/** 宣告＋版本清單。admin 以上。 */
export const listParamVersions = (productId: string) =>
  api<ParamVersionList>(`${base(productId)}/param-versions`)

/**
 * 設定（整份取代）參數宣告。營運主管以上。
 *
 * 新增的鍵讓目前生效／未來版本缺值時，`initialValues` 必須涵蓋那些鍵——缺了回 422
 * `initial_values_required`（`detail.missing_keys`）。授權中心在同一筆交易自動補一版立即
 * 生效的新版本、並補齊未來版本，結果在回應的 `backfill`。
 */
export const putParamDeclaration = (
  productId: string,
  declaration: ParamDeclaration,
  initialValues?: Record<string, string>,
) =>
  api<DeclarationResult>(`${base(productId)}/versioned-params`, {
    method: 'PUT',
    body: initialValues && Object.keys(initialValues).length
      ? { ...declaration, initial_values: initialValues }
      : declaration,
  })

/** 移除宣告＝停止下發（既有版本保留，重新宣告即恢復）。營運主管以上；冪等。 */
export const deleteParamDeclaration = (productId: string) =>
  api<DeclarationSpec>(`${base(productId)}/versioned-params`, { method: 'DELETE' })

/** 新增一版。營運主管以上。 */
export const createParamVersion = (productId: string, body: ParamVersionInput) =>
  api<ParamVersion>(`${base(productId)}/param-versions`, { method: 'POST', body })

/** 整份取代一個**未生效**的版本（已生效 409 `version_effective`）。營運主管以上。 */
export const updateParamVersion = (productId: string, versionId: string, body: ParamVersionInput) =>
  api<ParamVersion>(`${base(productId)}/param-versions/${encodeURIComponent(versionId)}`, {
    method: 'PUT',
    body,
  })

/** 刪除一個**未生效**的版本（已生效 409）。營運主管以上。 */
export const deleteParamVersion = (productId: string, versionId: string) =>
  api<null>(`${base(productId)}/param-versions/${encodeURIComponent(versionId)}`, { method: 'DELETE' })
